import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Sparkles, Save, ExternalLink, Check, X, Loader2, Camera, Crown, Copy } from "lucide-react";
import { toast } from "sonner";
import { handleApiBadges } from "@/lib/badges";

export default function CoachProfile() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', pseudo: '', punch_line: '', bio: '' });
  const [picture, setPicture] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [pictureData, setPictureData] = useState(null);
  const [pseudoStatus, setPseudoStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [aiLoading, setAiLoading] = useState(false);
  const [aiContext, setAiContext] = useState('');
  const [showAiDialog, setShowAiDialog] = useState(false);
  const pseudoTimer = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await api.get('/profile/me');
        setForm({
          name: data.name || '',
          pseudo: data.pseudo || '',
          punch_line: data.punch_line || '',
          bio: data.bio || '',
        });
        setPicture(data.picture);
      } catch {
        toast.error("Erreur lors du chargement du profil");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));

    if (field === 'pseudo') {
      const cleanValue = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
      setForm(prev => ({ ...prev, pseudo: cleanValue }));
      checkPseudo(cleanValue);
    }
  };

  const checkPseudo = useCallback((pseudo) => {
    if (pseudoTimer.current) clearTimeout(pseudoTimer.current);

    if (!pseudo || pseudo.length < 3) {
      setPseudoStatus(null);
      return;
    }

    setPseudoStatus('checking');
    pseudoTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/profile/check-pseudo/${pseudo}`);
        setPseudoStatus(data.available ? 'available' : 'taken');
      } catch {
        setPseudoStatus(null);
      }
    }, 500);
  }, []);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPicturePreview(ev.target.result);
      setPictureData(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.name || form.name.length < 2) {
      toast.error("Le nom doit contenir au moins 2 caractères");
      return;
    }
    if (form.pseudo && form.pseudo.length < 3) {
      toast.error("Le pseudo doit contenir au moins 3 caractères");
      return;
    }
    if (pseudoStatus === 'taken') {
      toast.error("Ce pseudo est déjà pris");
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (pictureData) {
        payload.picture_data = pictureData;
      }
      const res = await api.put('/profile/me', payload);
      const data = res.data;
      handleApiBadges(res, toast);
      setPicture(data.picture);
      setPictureData(null);
      setPicturePreview(null);
      await refreshUser();
      toast.success("Profil mis à jour !");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.error(detail || "Ce pseudo est déjà pris");
        setPseudoStatus('taken');
      } else {
        toast.error(detail || "Erreur lors de la sauvegarde");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiContext.trim()) {
      toast.error("Décrivez votre parcours et spécialités");
      return;
    }

    setAiLoading(true);
    try {
      const { data } = await api.post('/profile/generate-bio', { context: aiContext });
      setForm(prev => ({
        ...prev,
        bio: data.bio || prev.bio,
        punch_line: data.punch_line || prev.punch_line,
      }));
      setShowAiDialog(false);
      setAiContext('');
      toast.success("Bio générée par l'IA !");
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || "Erreur lors de la génération";
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const copyProfileUrl = () => {
    const url = `${window.location.origin}/@${form.pseudo}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié !");
  };

  const isPro = user?.has_active_subscription;
  const displayPicture = picturePreview || picture;
  const initials = form.name
    ? form.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
            Profil Public
          </h1>
          <p className="text-slate-500 mt-1">Personnalisez votre page publique pour attirer de nouveaux élèves.</p>
        </div>
        {form.pseudo && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyProfileUrl} className="rounded-full gap-2">
              <Copy className="w-3.5 h-3.5" /> Copier le lien
            </Button>
            <a href={`/@${form.pseudo}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="rounded-full gap-2">
                <ExternalLink className="w-3.5 h-3.5" /> Voir mon profil
              </Button>
            </a>
          </div>
        )}
      </div>

      {/* Photo + Name Card */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Avatar */}
          <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
            {displayPicture ? (
              <img
                src={displayPicture}
                alt="Photo de profil"
                className="w-28 h-28 rounded-full object-cover ring-4 ring-slate-100"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center ring-4 ring-slate-100">
                <span className="text-3xl font-bold text-white">{initials}</span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoSelect}
            />
          </div>

          {/* Name + Pseudo */}
          <div className="flex-1 space-y-4 w-full">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nom complet</label>
              <Input
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder="Votre nom"
                className="h-10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Pseudo (URL publique)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
                <Input
                  value={form.pseudo}
                  onChange={e => handleChange('pseudo', e.target.value)}
                  placeholder="monpseudo"
                  className="h-10 pl-7 pr-10"
                  maxLength={30}
                />
                {pseudoStatus === 'checking' && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                )}
                {pseudoStatus === 'available' && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                )}
                {pseudoStatus === 'taken' && (
                  <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {form.pseudo && (
                <p className="text-xs text-slate-400 mt-1">
                  Votre page publique : {window.location.origin}/@{form.pseudo}
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Punch Line + Bio Card */}
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Présentation</h2>
          {isPro ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAiDialog(true)}
              className="rounded-full gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
            >
              <Sparkles className="w-3.5 h-3.5" /> Générer avec l'IA
            </Button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full">
              <Crown className="w-3.5 h-3.5" /> IA réservée aux Pro
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Phrase d'accroche</label>
          <Input
            value={form.punch_line}
            onChange={e => handleChange('punch_line', e.target.value)}
            placeholder="Ex: Transformez votre corps et votre mindset en 12 semaines"
            className="h-10"
            maxLength={255}
          />
          <p className="text-xs text-slate-400 mt-1 text-right">{form.punch_line.length}/255</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Bio</label>
          <textarea
            value={form.bio}
            onChange={e => handleChange('bio', e.target.value)}
            placeholder="Parlez de votre parcours, vos spécialités, votre approche..."
            className="w-full min-h-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y"
            maxLength={2000}
          />
          <p className="text-xs text-slate-400 mt-1 text-right">{form.bio.length}/2000</p>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 h-11 shadow-lg shadow-blue-500/20"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sauvegarde...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Sauvegarder</>
          )}
        </Button>
      </div>

      {/* AI Dialog */}
      {showAiDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAiDialog(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">Générer avec l'IA</h3>
                <p className="text-xs text-slate-500">Décrivez-vous et l'IA créera votre bio et phrase d'accroche</p>
              </div>
            </div>

            <textarea
              value={aiContext}
              onChange={e => setAiContext(e.target.value)}
              placeholder="Ex: Je suis coach sportif depuis 8 ans, spécialisé en musculation et nutrition. J'ai accompagné plus de 200 clients dans leur transformation physique. Mon approche est basée sur la science et l'écoute..."
              className="w-full min-h-[140px] rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-y mb-4"
            />

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAiDialog(false)} className="rounded-full">
                Annuler
              </Button>
              <Button
                onClick={handleAiGenerate}
                disabled={aiLoading || !aiContext.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full gap-2"
              >
                {aiLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Génération...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Générer</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
